import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    HostListener,
    inject,
    input,
    OnChanges,
    OnInit,
    signal,
    SimpleChanges
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import {
    faArrowRightFromBracket as falArrowRightFromBracket,
    faBell as falBell,
    faFileLines as falFileLines,
    faGear as falGear,
    faHouseChimney as falHouseChimney,
    faNoteSticky as falNoteSticky,
    faPresentation as falPresentation,
    faUserGear as falUserGear,
    faUserHeadset as falUserHeadset,
    faUsers as falUsers
} from '@fortawesome/pro-light-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LogoComponent, SectionSeparatorComponent, tryToUnsubscribeFromSubscription } from '@swissfex/sfx-commons';
import { filter, Observable, Subscription } from 'rxjs';

import { TranslateRoutePipe } from '../../common-utils/pipes/translate-route.pipe';
import {
    EDIT_PROJECT_PATHS,
    LOGIN_QUERY_LOGOUT_REASON_MAP,
    LOGOUT_REASON,
    MAIN_ROUTES,
    OUTLET_NAMES,
    PATH_NAMES_PER_OUTLETS,
    PROJECT_PAGE_URL_SUBITEMS_TEMPLATE,
    SIDEBAR_NAVIGATION_COMPACT_TRIGGER_WIDTH,
    SIDEBAR_NAVIGATION_ITEMS_TITLE
} from '../../constants';
import { IApplicationUser } from '../../core/models/IApplicationUser';
import { RealTimeNotificationService } from '../../core/services/real-time-notification.service';
import { RouterService } from '../../core/services/router.service';
import { DocumentUtils } from '../../core/utils/document.utils';
import { ProjectUtils } from '../../core/utils/project.utils';
import { RouterUtils } from '../../core/utils/router.utils';
import { ProjectStatus } from '../../projects/models/enums/projectStatus';
import { INews } from '../../projects/models/INews';
import { IPrivacySettings } from '../../projects/models/IPrivacySettings';
import { IProject } from '../../projects/models/IProject';
import { IRequiredDocuments } from '../../projects/models/IRequiredDocuments';
import { AuthenticationService } from '../../projects/services/authentication.service';
import { AuthorizationService } from '../../projects/services/authorization.service';
import { DocumentsService } from '../../projects/services/documents.service';
import { NewsService } from '../../projects/services/news.service';
import { PrivacyService } from '../../projects/services/privacy.service';
import { ProjectsService } from '../../projects/services/projects.service';
import { ISidebarItem, SidebarItemComponent, SidebarItemHighlightType } from '../sidebar-item/sidebar-item.component';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-sidebar-navigation',
    templateUrl: './sidebar-navigation.component.html',
    styleUrls: ['./sidebar-navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterLink,
        RouterLinkActive,
        SidebarItemComponent,
        TranslateRoutePipe,
        SectionSeparatorComponent,
        LogoComponent,
        AsyncPipe
    ]
})
export class SidebarNavigationComponent implements OnChanges, OnInit {
    private readonly authenticationSvc = inject(AuthenticationService);
    private readonly authorizationSvc = inject(AuthorizationService);
    private readonly documentsSvc = inject(DocumentsService);
    private readonly newsSvc = inject(NewsService);
    private readonly privacySvc = inject(PrivacyService);
    private readonly projectSvc = inject(ProjectsService);
    private readonly realTimeNotificationSvc = inject(RealTimeNotificationService);
    private readonly routerSvc = inject(RouterService);

    public urlProjectId = input<number>();

    public isNavigationGuardOrResolverInProgress$: Observable<boolean>;
    public isCompact = signal(false);
    public currentUser = signal<IApplicationUser>(undefined);
    public isAnonymized = signal(false);
    public projectSpecificNavItems = computed((): ISidebarItem[] => {
        if (!this.project()) {
            return [];
        }

        return [
            this.teamSellingNavItem(),
            this.descriptionNavItem(),
            this.documentsPageNavItem(),
            this.documentsModalNavItem()
        ].filter((item) => item.isVisible);
    });

    public bottomNavItems = computed((): ISidebarItem[] =>
        [
            this.newsNavItem(),
            this.serviceCenterNavItem,
            // this.feedbackNavItem,
            this.settingsNavItem,
            this.userProfileNavItem(),
            this.logoutNavItem()
        ].filter((item) => item.isVisible)
    );

    public presenterViewNavItem = computed((): ISidebarItem => ({
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.presenterView,
        icon: falPresentation,
        isVisible: true,
        extraClasses: this.commonSidebarItemExtraClass,
        isInverted: this.isAnonymized(),
        onClick: this.togglePrivacyMode
    }));

    public readonly homeNavItem: ISidebarItem = {
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.home,
        icon: falHouseChimney,
        routeSubItems: [MAIN_ROUTES.PROJECTS],
        routerLinkActive: 'mad--selected mad--disabled mad-navigation-bar-item--disabled',
        routerLinkActiveOptions: {
            queryParams: 'ignored',
            paths: 'exact',
            matrixParams: 'ignored',
            fragment: 'ignored'
        },
        isVisible: true
    };

    private documentsModalNavItem = computed((): ISidebarItem => ({
        id: 4,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.documentsModal,
        icon: falFileLines,
        outstandingNotifications: 0,
        routeSubItems: PROJECT_PAGE_URL_SUBITEMS_TEMPLATE,
        routeSubItemParams: this.commonProjectSpecificNavItemRouteSubItemParams(),
        isVisible: !this.documentsPageNavItem().isVisible,
        highlightType: this.documentNavItemHighlightType(),
        outlet: { [OUTLET_NAMES.DOCUMENT_MODAL_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.DOCUMENT_MODAL_OUTLET] }
    }));

    private documentsPageNavItem = computed((): ISidebarItem => ({
        id: 3,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.documentsPage,
        icon: falFileLines,
        outstandingNotifications: 0,
        isVisible: ProjectUtils.canAccessDocumentsPageForProject(this.project()),
        routeSubItems: [...PROJECT_PAGE_URL_SUBITEMS_TEMPLATE, EDIT_PROJECT_PATHS.DOCUMENTS],
        routeSubItemParams: this.commonProjectSpecificNavItemRouteSubItemParams(),
        isAbsolute: false,
        queryParamsHandling: 'preserve',
        highlightType: this.documentNavItemHighlightType()
    }));

    private descriptionNavItem = computed((): ISidebarItem => ({
        id: 2,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.description,
        icon: falNoteSticky,
        outstandingNotifications: 0,
        routeSubItems: PROJECT_PAGE_URL_SUBITEMS_TEMPLATE,
        routeSubItemParams: this.commonProjectSpecificNavItemRouteSubItemParams(),
        isVisible: true,
        outlet: {
            [OUTLET_NAMES.PROJECT_DESCRIPTION_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.PROJECT_DESCRIPTION_OUTLET]
        },
        queryParamsHandling: 'preserve'
    }));

    private teamSellingNavItem = computed((): ISidebarItem => ({
        id: 1,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.teamSelling,
        icon: falUsers,
        outstandingNotifications: 0,
        routeSubItems: PROJECT_PAGE_URL_SUBITEMS_TEMPLATE,
        routeSubItemParams: this.commonProjectSpecificNavItemRouteSubItemParams(),
        isVisible: RouterUtils.canUserAccessTeamSelling(
            this.currentUser(),
            this.project()?.owner?.id,
            this.project()?.owner?.tenantSettings?.isTeamSellingEnabled,
            this.hasTeamSellingAccessPermission()
        ),
        outlet: {
            [OUTLET_NAMES.TEAM_SELLING_MODAL_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.TEAM_SELLING_MODAL_OUTLET]
        },
        queryParamsHandling: 'preserve'
    }));

    private userProfileNavItem = computed((): ISidebarItem => {
        let title = '';
        let hideCollapsed = true;
        let extraClasses = this.commonSidebarItemExtraClass;

        if (this.isBackofficeUser()) {
            hideCollapsed = false;
            extraClasses = `${this.commonSidebarItemExtraClass} ${this.isWebSocketConnected() ? this.webSocketConnectedClass : this.webSocketDisconnectedClass}`;
        }

        if (this.currentUser()) {
            title = `${this.currentUser().firstname || ''} ${this.currentUser().lastname || ''}`.trim();
        }

        return {
            routeSubItems: ['profile'],
            icon: falUserGear,
            outstandingNotifications: 0,
            isDisabled: true,
            isVisible: true,
            id: 5,
            title,
            extraClasses,
            hideCollapsed
        };
    });

    private newsNavItem = computed((): ISidebarItem => ({
        id: 1,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.news,
        icon: falBell,
        isVisible: !!this.news()?.length,
        routeSubItems: [MAIN_ROUTES.PROJECTS],
        outlet: { [OUTLET_NAMES.NEWS_MODAL_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.NEWS_MODAL_OUTLET] },
        queryParamsHandling: 'preserve'
    }));

    private logoutNavItem = computed((): ISidebarItem => ({
        id: 6,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.logout,
        icon: falArrowRightFromBracket,
        isVisible: this.isAuthenticated(),
        routeSubItems: [MAIN_ROUTES.LOGOUT],
        isAbsolute: true,
        queryParams: { [LOGIN_QUERY_LOGOUT_REASON_MAP[LOGOUT_REASON.USER_LOGOUT]]: LOGOUT_REASON.USER_LOGOUT }
    }));

    private documentNavItemHighlightType = computed(() => {
        if (!this.project()) {
            return null;
        }

        return this.project().projectStatus === ProjectStatus.AdditionalDocumentsRequestedByCC
            ? SidebarItemHighlightType.Error
            : this.requiredDocuments()?.projectId === this.project().id &&
                DocumentUtils.areThereAnyNewValidDocuments(this.documentsSvc)
              ? SidebarItemHighlightType.Warning
              : null;
    });

    private commonProjectSpecificNavItemRouteSubItemParams = computed(() =>
        this.project()?.id ? [this.project().id] : null
    );

    private isBackofficeUser = signal(false);
    private isWebSocketConnected = signal(false);
    private news = signal<INews[]>(undefined);
    private isAuthenticated = signal(false);
    private requiredDocuments = signal<IRequiredDocuments>(undefined);
    private project = signal<IProject>(undefined);
    private hasTeamSellingAccessPermission = signal(false);
    private cachedProjectSubscription: Subscription;
    private readonly commonSidebarItemExtraClass = 'nav-max-width';
    private readonly webSocketConnectedClass = 'web-socket-connection-status__connected';
    private readonly webSocketDisconnectedClass = 'web-socket-connection-status__disconnected';
    private readonly serviceCenterNavItem: ISidebarItem = {
        id: 2,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.serviceCenter,
        extraClasses: this.commonSidebarItemExtraClass,
        icon: falUserHeadset,
        isVisible: true,
        routeSubItems: [MAIN_ROUTES.PROJECTS],
        outlet: {
            [OUTLET_NAMES.SERVICE_CENTER_MODAL_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.SERVICE_CENTER_MODAL_OUTLET]
        },
        queryParamsHandling: 'preserve'
    };
    // Info: Commenting this out temporarily, until we have a new feedback system in place.
    // private readonly feedbackNavItem: ISidebarItem = {
    //     id: 3,
    //     title: SIDEBAR_NAVIGATION_ITEMS_TITLE.feedback,
    //     extraClasses: this.commonSidebarItemExtraClass,
    //     icon: falMessagePen,
    //     isVisible: false,
    //     onClick: () => null
    // };
    private readonly settingsNavItem: ISidebarItem = {
        id: 4,
        title: SIDEBAR_NAVIGATION_ITEMS_TITLE.settings,
        extraClasses: this.commonSidebarItemExtraClass,
        icon: falGear,
        isVisible: true,
        routeSubItems: [MAIN_ROUTES.PROJECTS],
        outlet: {
            [OUTLET_NAMES.SETTINGS_MODAL_OUTLET]: PATH_NAMES_PER_OUTLETS[OUTLET_NAMES.SETTINGS_MODAL_OUTLET]
        },
        queryParamsHandling: 'preserve'
    };

    constructor() {
        this.checkAndToggleClass(window.innerWidth);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.urlProjectId) {
            tryToUnsubscribeFromSubscription(this.cachedProjectSubscription);
            if (this.urlProjectId()) {
                this.cachedProjectSubscription = this.projectSvc
                    .cachedProjectObservable(this.urlProjectId())
                    .pipe(
                        untilDestroyed(this),
                        filter((project) => !project || !!project.projectStatus)
                    )
                    .subscribe((project) => {
                        this.onProjectReceived(project);
                    });
            } else {
                this.onProjectReceived(null);
            }
        }
    }

    public ngOnInit(): void {
        this.privacySvc.privacyObservable.pipe(untilDestroyed(this)).subscribe(({ isAnonymized }: IPrivacySettings) => {
            this.isAnonymized.set(isAnonymized);
        });

        this.authenticationSvc.authStatus.pipe(untilDestroyed(this)).subscribe((authData) => {
            this.isAuthenticated.set(!!authData?.isAuthenticated);
        });

        this.authenticationSvc.currentUser$.pipe(untilDestroyed(this)).subscribe((currentUser) => {
            this.currentUser.set(currentUser);
            this.isBackofficeUser.set(this.authenticationSvc.isSfxAdmin);
        });

        this.authorizationSvc.openedProjectPermissionMapObservable
            .pipe(untilDestroyed(this))
            .subscribe((projectPermissionMap) => {
                this.hasTeamSellingAccessPermission.set(!!projectPermissionMap?.PP_TeamSellingReadOnlyAccess);
            });

        this.documentsSvc.requiredDocumentsObservable.pipe(untilDestroyed(this)).subscribe((requiredDocuments) => {
            this.requiredDocuments.set(requiredDocuments);
        });

        this.realTimeNotificationSvc.isWebSocketConnected
            .pipe(untilDestroyed(this))
            .subscribe((isConnected: boolean) => {
                this.isWebSocketConnected.set(isConnected);
            });

        this.newsSvc.news$.pipe(untilDestroyed(this)).subscribe((news: INews[]) => {
            this.news.set(news);
        });

        this.isNavigationGuardOrResolverInProgress$ = this.routerSvc.isNavigationGuardOrResolverInProgress$;
    }

    public togglePrivacyMode = () => {
        this.privacySvc.setAnonymization(!this.isAnonymized());
    };

    @HostListener('window:resize', ['$event'])
    public onResize(event) {
        this.checkAndToggleClass(event.target.innerWidth);
    }

    private checkAndToggleClass(width: number) {
        this.isCompact.set(width <= SIDEBAR_NAVIGATION_COMPACT_TRIGGER_WIDTH);
    }

    private onProjectReceived(project: IProject): void {
        if (this.urlProjectId() && !!project && this.urlProjectId() === project.id) {
            this.project.set(project);
        } else {
            this.project.set(null);
        }
    }
}
